/**
 * @module tools/singletone
 *
 * @private
 *
 * @description
 * Помощник для создания синглтонов из классов
 *
 * @param {Object} _Class - Класс, из которого делаем сингл
 *
 * @return {Object} Экземпляр класса _Class
 */
export default (_Class: any) => {
  let instance: any;

  return {
    getInstance: () => {
      if (!instance) {
        instance = new _Class();
      }

      return instance;
    }
  };
};

type SingletonFactory = { getInstance: () => any };

/**
 * Ленивый default-export: конструктор синглтона вызывается при первом обращении к прокси.
 * Цель — уменьшить Script Evaluation в core-бандле.
 *
 * Второй аргумент — конструктор инстанса, чтобы у прокси была цепочка прототипов
 * и срабатывал instanceof (например proxy instanceof Cart).
 */
export function deferredSingletonExport(
  factory: SingletonFactory,
  Constructor: new (...args: any[]) => any
): any {
  const protoTarget = Object.create(Constructor.prototype);

  const resolve = () => factory.getInstance();

  let proxy: any;
  proxy = new Proxy(protoTarget, {
    get(_target, prop, receiver) {
      const instance = resolve();
      const value = Reflect.get(instance, prop, receiver);
      if (typeof value === 'function') {
        return function wrappedMethod(this: unknown, ...args: unknown[]) {
          const ret = Reflect.apply(value as (...a: unknown[]) => unknown, instance, args);
          return ret === instance ? proxy : ret;
        };
      }
      return value;
    },

    set(_target, prop, value, receiver) {
      return Reflect.set(resolve(), prop, value, receiver);
    },

    has(_target, prop) {
      return Reflect.has(resolve(), prop);
    },

    deleteProperty(_target, prop) {
      return Reflect.deleteProperty(resolve(), prop);
    },

    getOwnPropertyDescriptor(_target, prop) {
      return Reflect.getOwnPropertyDescriptor(resolve(), prop);
    },

    defineProperty(_target, prop, desc) {
      return Reflect.defineProperty(resolve(), prop, desc);
    },

    ownKeys(_target) {
      return Reflect.ownKeys(resolve());
    }
  });

  return proxy;
}
